/**
 * Project theme — two-tier design.
 *
 * Tier 1 — Primitives (`--theme-font-*, --theme-colour-*`)
 * ----------------------------------------
 * Raw palette values that get reused. Named by appearance
 * (`muted-grey`, `tan-grey`, `live-red`) so the name tells you what
 * pigment it is, not where it's used. Override these to rebrand the
 * whole palette in one go.
 *
 * Tier 2 — Semantic tokens (`--theme-style-*`)
 * --------------------------------------------
 * Purpose-driven aliases that reference the primitives via `var()`.
 * Colour-bearing tokens are suffixed `-colour`; length-
 * bearing tokens carry no suffix (e.g. `border-radius-pill`).
 *
 * Components consume semantic tokens by intent
 * (`card-background-colour`, `status-live-colour`,
 * `border-radius-pill`) rather than reaching for a raw primitive.
 * This is the layer a client override should target to rebrand a
 * *category* (every card surface, every "live" indicator) without
 * having to know which underlying pigment is involved.
 *
 * What we deliberately don't set
 * ------------------------------
 * `--theme-colour-brand-{logo,rules,shadow}` come from the
 * `@reuters-graphics/graphics-components` default theme and represent
 * the corporate Reuters brand. They are intentionally *not* overridden
 * here. If the app needs a brand colour, reference it through a
 * semantic alias defined below (e.g.
 * `'border-subtle-colour': 'var(--theme-colour-brand-rules)'`) so
 * consumers never reach across the brand boundary themselves.
 *
 */

const BORDER_RADIUS_BREAKPOINT_PX = 475;

// ── Tier 1: primitives (--theme-font-*, --theme-colour-*) ────────────────────────────
const font = {
  family: {
    hed: 'Outfit, var(--theme-font-family-sans-serif), sans-serif',
  },
};

const colour = {
  // Neutrals
  white: '#fff',
  'muted-grey': '#f3f4f6',
  'tan-grey': '#e5e5e5',
  'mid-grey': '#ccc',
  'dark-grey': '#aaa',

  // Text
  'text-primary': '#212223',
  'text-secondary': '#595959',

  // Project brand
  logo: '#004f9f',

  // Status palette
  'win-green': '#e1f4cd',
  'loss-red': '#efb399',
  'live-red': '#d64000',
  'pitch-green': '#6aa86a',
  'trophy-gold': '#DDBA60',
} as Record<string, string>;

// ── Tier 2: semantic tokens (--theme-style-*) ────────────────────────
const getStyle = (windowWidth: number) => ({
  // ── Shape ────────────────────────────────────────────────────────
  'border-radius':
    windowWidth > BORDER_RADIUS_BREAKPOINT_PX ? '0.75rem' : '0.5rem',
  'border-radius-pill': '5rem',

  // ── Cards & surfaces ─────────────────────────────────────────────
  'card-background-colour': 'var(--theme-colour-muted-grey)',
  'card-border-colour': 'var(--theme-colour-tan-grey)',
  'card-flag-placeholder-colour': 'var(--theme-colour-tan-grey)',
  'card-outline-colour': 'var(--theme-colour-white)',

  // ── Pitch (lineup view) ──────────────────────────────────────────
  'pitch-surface-colour': 'var(--theme-colour-pitch-green)',
  'pitch-line-colour': 'rgba(255, 255, 255, 0.75)',

  // ── Borders / dividers ───────────────────────────────────────────
  'commentary-border-colour': 'var(--theme-colour-tan-grey)',
  'border-subtle-colour': 'var(--theme-colour-brand-rules)',

  // ── Status ───────────────────────────────────────────────────────
  'status-live-colour': 'var(--theme-colour-live-red)',
  'status-win-colour': 'var(--theme-colour-win-green)',
  'status-loss-colour': 'var(--theme-colour-loss-red)',

  // ── Decorative / illustrations ──────────────────────────────────
  'trophy-fill-colour': 'var(--theme-colour-trophy-gold)',
  'trophy-shadow-colour': 'var(--theme-colour-mid-grey)',
  'trophy-runner-up-colour': 'var(--theme-colour-muted-grey)',

  // ── Overlays & alpha tints ──────────────────────────────────────
  'soft-shadow-colour': 'rgba(0, 0, 0, 0.15)',
  'overlay-dark-colour': 'rgba(0, 0, 0, 0.5)',
  'pitch-line-faint-colour': 'rgba(255, 255, 255, 0.5)',
});

/**
 * Build the `theme` prop for `<Theme>`.
 *
 * Pass the current window width (typically via
 * `<svelte:window bind:innerWidth />`) so the responsive
 * `--theme-style-border-radius` resolves correctly.
 */
export const getTheme = (windowWidth: number) =>
  ({
    font,
    colour,
    style: getStyle(windowWidth),
  }) as const;

export type ProjectTheme = ReturnType<typeof getTheme>;
